import { useMemo, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';
import { MONTHLY_FINALIZED_TABLE } from '../../../constants/tableColumns';
import { routeConstants } from '../../../constants/routeConstants';
import { setFinalizedFilters, resetFinalizedFilters } from '../../../redux-slice/monthlyReconciliationFilters';
import { useGetFinalizedMonthlyPostingsQuery } from '../../../services/GlPostings/GlPostingsService';
import { useGetProductCategoriesQuery } from '../../../services/Sublocation/SublocationService';
import { useGetDropdownUsersQuery } from '../../../services/Users/UsersService';
import MonthlyReconciliationTab from './MonthlyReconciliationTab';
import { buildFinalizedFilterOptions, getProductTypeNamesFromCategories } from './helpers/buildFilterOptions';
import { SEARCH_PLACEHOLDER, SORT_COMPARATORS } from './constants';
import {
  buildFinalizedQueryParams,
  buildFinalizedSortParam,
  mapPostingToFinalizedRow,
  parseFinalizedSortParam,
  selectFinalizedFilters,
  userListToCreatedByOptions,
} from './helpers/monthlyFinalizedTabHelpers';

const FinalizedTab = () => {
  const dispatch = useDispatch();
  const filterParams = useSelector(selectFinalizedFilters);
  const queryParams = useMemo(
    () => buildFinalizedQueryParams(filterParams, DEFAULT_PAGE, DEFAULT_PER_PAGE),
    [filterParams],
  );
  const sortState = useMemo(() => parseFinalizedSortParam(filterParams.sort), [filterParams.sort]);
  const handleSortChange = useCallback(
    (columnId, desc) => {
      dispatch(setFinalizedFilters({ sort: buildFinalizedSortParam(columnId, desc), pageNumber: DEFAULT_PAGE }));
    },
    [dispatch],
  );
  const { data: apiData, isLoading: isQueryLoading } = useGetFinalizedMonthlyPostingsQuery(queryParams);
  const apiPostings = apiData?.postings ?? [];
  const totalCount = apiData?.pagination?.total ?? 0;

  const handlePageChange = useCallback((page) => dispatch(setFinalizedFilters({ pageNumber: page })), [dispatch]);
  const handlePerPageChange = useCallback(
    (rowsPerPage) => dispatch(setFinalizedFilters({ rowsPerPage, pageNumber: DEFAULT_PAGE })),
    [dispatch],
  );
  const { data: productCategories = [] } = useGetProductCategoriesQuery({ isGLPostingEnabled: true });
  const { data: usersResponse, isLoading: isUsersLoading } = useGetDropdownUsersQuery({
    page: 1,
    perPage: 1000,
    searchParams: '',
  });
  const createdByOptions = useMemo(() => {
    const users = usersResponse?.users;
    if (!Array.isArray(users)) return [];
    return userListToCreatedByOptions(users);
  }, [usersResponse]);

  const tableData = useMemo(() => {
    const list = Array.isArray(apiPostings) ? apiPostings : [];
    return list.map((posting) => mapPostingToFinalizedRow(posting, productCategories));
  }, [apiPostings, productCategories]);

  const productTypeNames = useMemo(() => getProductTypeNamesFromCategories(productCategories), [productCategories]);
  const filterOptions = useMemo(() => {
    const base = buildFinalizedFilterOptions(productTypeNames);
    return {
      ...base,
      [FILTER_VALUES.CREATED_BY]: createdByOptions.length > 0 ? createdByOptions : base[FILTER_VALUES.CREATED_BY],
    };
  }, [productTypeNames, createdByOptions]);

  const isLoading = isQueryLoading || isUsersLoading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MonthlyReconciliationTab
      tabKey="finalized"
      data={tableData}
      filterDefinition={FILTER_CONSTANTS.FILTER_DATA.MONTHLY_FINALIZED}
      setFiltersAction={setFinalizedFilters}
      resetFiltersAction={resetFinalizedFilters}
      columns={MONTHLY_FINALIZED_TABLE}
      sortComparators={SORT_COMPARATORS.finalized}
      detailRoute={routeConstants.MONTHLY_APPROVED_DETAIL_ROUTE}
      filterOptions={filterOptions}
      noDataTitleMessageKey="NO_FILTERED_DATA_TITLE_APPROVED"
      searchPlaceholder={SEARCH_PLACEHOLDER}
      initialSortBy={{ id: 'finalizedDate', desc: true }}
      sortBy={sortState}
      onSortChange={handleSortChange}
      totalCount={totalCount}
      pageNumber={filterParams.pageNumber ?? DEFAULT_PAGE}
      rowsPerPage={filterParams.rowsPerPage ?? DEFAULT_PER_PAGE}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
    />
  );
};

export default FinalizedTab;
